import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight, Tag } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { blogPosts as mockPosts } from '../../data/mock';
import { useContent } from '../../context/ContentContext';
import api from '../../lib/api';

const NewsPage = () => {
  const { c } = useContent();
  const [blogPosts, setBlogPosts] = React.useState(mockPosts);
  React.useEffect(() => {
    api.get('/news').then((res) => { if (res.data?.length) setBlogPosts(res.data); }).catch(() => {});
  }, []);
  return (
    <main className="pt-52">
      {/* Hero Section */}
      <section className="py-20 bg-[#f1e3eb]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#bd8cae] text-sm uppercase tracking-wider font-medium">
              {c('news.hero.eyebrow', 'Stay Informed')}
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#3c4a2a] mt-3 mb-6">
              {c('news.hero.title', 'News & Updates')}
            </h1>
            <p className="text-lg text-[#3c4a2a]/80 leading-relaxed">
              {c('news.hero.text', 'Stories of impact, announcements, and highlights from our community of women leaders in Lincoln County.')}
            </p>
          </div>
        </div>
      </section>

      {/* Featured Post */}
      {blogPosts.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="overflow-hidden border-0 shadow-xl">
              <div className="grid lg:grid-cols-2">
                <div className="aspect-[16/10] lg:aspect-auto">
                  <img
                    src={blogPosts[0].image}
                    alt={blogPosts[0].title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-8 lg:p-12 flex flex-col justify-center">
                  <Badge className="w-fit bg-[#bd8cae]/50 text-[#bd8cae] mb-4">
                    {blogPosts[0].category}
                  </Badge>
                  <h2 className="font-serif text-2xl sm:text-3xl text-[#3c4a2a] mb-4">
                    {blogPosts[0].title}
                  </h2>
                  {blogPosts[0].content ? (
                    <div className="space-y-3 mb-6">
                      {blogPosts[0].content.split('\n').filter(Boolean).map((para, i) => (
                        <p key={para} className="text-[#3c4a2a]/70" data-testid={`featured-article-p${i + 1}`}>{para}</p>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[#3c4a2a]/70 mb-6">
                      {blogPosts[0].excerpt}
                    </p>
                  )}
                  <div className="flex items-center text-sm text-[#3c4a2a]/60 mb-6">
                    <User className="w-4 h-4 mr-2" />
                    <span className="mr-4">{blogPosts[0].author}</span>
                    <Calendar className="w-4 h-4 mr-2" />
                    <span>
                      {new Date(blogPosts[0].date).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </span>
                  </div>
                  {blogPosts[0].content ? (
                    <Link to="/founding-members">
                      <Button className="w-fit bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white" data-testid="featured-interest-btn">
                        Express Your Interest
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  ) : (
                    <Button className="w-fit bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white">
                      Read Full Article
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                </CardContent>
              </div>
            </Card>
          </div>
        </section>
      )}

      {/* More Posts */}
      <section className="py-16 bg-[#f1e3eb]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-2xl text-[#3c4a2a] mb-8">More Stories</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.slice(1).map((post) => (
              <Card
                key={post.id}
                className="bg-white border-0 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-6">
                  <Badge className="bg-[#bd8cae]/50 text-[#bd8cae] mb-3">
                    {post.category}
                  </Badge>
                  <h3 className="font-serif text-xl text-[#3c4a2a] mb-2 line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-[#3c4a2a]/70 text-sm mb-4 line-clamp-2">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-[#3c4a2a]/60">
                    <span>{post.author}</span>
                    <span>
                      {new Date(`${post.date}T12:00:00`).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Placeholder for more posts */}
          <div className="mt-12 text-center">
            <p className="text-[#3c4a2a]/60 mb-4">More stories coming soon</p>
            <Button
              variant="outline"
              className="border-[#5d6b3c] text-[#5d6b3c] hover:bg-[#5d6b3c] hover:text-white"
              disabled
            >
              Load More
            </Button>
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 bg-[#5d6b3c]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl text-white mb-4">
            Never Miss an Update
          </h2>
          <p className="text-white/90 mb-8">
            Subscribe to our newsletter for the latest news, events, and stories 
            from our community.
          </p>
          <Link to="/#newsletter">
            <Button className="bg-[#bd8cae] hover:bg-[#bd8cae] text-[#3c4a2a] px-8">
              Subscribe Now
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
};

export default NewsPage;
